import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useTenant } from '../../context/TenantContext';

const HOST = 'localhost:8080';

interface Field {
  name: string;
  type: string;
  note: string;
}

const RIDE_REQUIRED: Field[] = [
  { name: 'id', type: 'int', note: 'Yours to choose; re-posting the same id replaces the ride' },
  { name: 'pickup_at', type: 'UTC datetime', note: '"yyyy-MM-dd HH:mm:ss"' },
  { name: 'dropoff_at', type: 'UTC datetime', note: 'Must be after pickup_at' },
  { name: 'pickup_lat / pickup_lon', type: 'double', note: 'Where the rider is collected' },
  { name: 'dropoff_lat / dropoff_lon', type: 'double', note: 'Where the rider is dropped' }
];

const RIDE_OPTIONAL =
  'company_id (what a run scopes on — name it with POST /api/v1/companies and the run screen ' +
  'offers it by name), duration_seconds (default: the pickup→dropoff gap), ' +
  'distance_miles (default: straight-line), seats, user_id, ride_type_id, pickup_zone_id, dropoff_zone_id, driver_net, ' +
  'cash_amount, amount, wav, pooling_enabled, pool_group, suv_restricted, sedan_only, reference_id';

const ROUTE_REQUIRED: Field[] = [
  { name: 'id', type: 'int', note: 'Yours to choose; re-posting the same id replaces the route' },
  { name: 'from_datetime', type: 'UTC datetime', note: 'Shift start — also what puts the route in a planned day' },
  { name: 'to_datetime', type: 'UTC datetime', note: 'Must be after from_datetime' },
  {
    name: 'starting_zip_code',
    type: 'string',
    note: 'Where the driver starts — or give start_lat / start_lon if you have coordinates'
  }
];

const ROUTE_OPTIONAL =
  'start_lat / start_lon (coordinates instead of a zip code — when both are given the zip wins, ' +
  'as it does on the platform), ' +
  'driver_id, car_id, fleet_id (groups the route under a fleet — what the run screen offers), ' +
  'blacklisted_ride_ids, start_zone_id, zone_ids, seats, wav_seats, wav_car, car_type, ' +
  'allowed_company_ids, banned_company_ids, allowed_ride_type_ids, fleet_enabled_company_ids';

const DRIVER_REQUIRED: Field[] = [
  { name: 'id', type: 'int', note: 'The driver_id the routes carry; re-posting replaces the entry' }
];

const DRIVER_OPTIONAL =
  'name, and a vehicle object of car_id, seats, wav_seats, wav_car, car_type, plate, make_model';

export const ApiGuidePage = () => {
  const { tenant } = useTenant();
  const { enqueueSnackbar } = useSnackbar();

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      enqueueSnackbar('Copied');
    } catch {
      enqueueSnackbar('Copying is blocked in this browser — select the text instead', {
        variant: 'error'
      });
    }
  };

  const Snippet = ({ code }: { code: string }) => (
    <Box sx={{ position: 'relative', mt: 1 }}>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          overflowX: 'auto',
          borderRadius: 1,
          bgcolor: 'action.hover',
          fontFamily: 'monospace',
          fontSize: 12,
          lineHeight: 1.6
        }}
      >
        {code}
      </Box>
      <Button
        size="small"
        aria-label={`copy ${code.split('\n')[0]}`}
        onClick={() => copy(code)}
        sx={{ position: 'absolute', top: 4, right: 4 }}
      >
        Copy
      </Button>
    </Box>
  );

  const Fields = ({ rows, optional }: { rows: Field[]; optional: string }) => (
    <>
      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '30%' }}>Required field</TableCell>
              <TableCell sx={{ width: '20%' }}>Type</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(field => (
              <TableRow key={field.name}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{field.name}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{field.type}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{field.note}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        <strong>Optional</strong> — everything else defaults to the least restrictive value:{' '}
        {optional}.
      </Typography>
    </>
  );

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          LOADING A WORLD FOR “{tenant}”
        </Typography>
        <Typography variant="body2" color="text.secondary">
          A run plans exactly the rides and routes you POST here — this service has no other
          source of data. Do it in this order; every example uses the tenant picked in the top
          bar.
        </Typography>
        <Typography variant="body2" color="text.secondary" component="ol" sx={{ pl: 3, mt: 1 }}>
          <li>Create the tenant (once) — or the Admin tab.</li>
          <li>Name its fleets and companies (once) so ids show as names.</li>
          <li>Register the drivers and what each one drives.</li>
          <li>POST the rides — the demand.</li>
          <li>POST the routes — the drivers, each optionally under a fleet.</li>
          <li>Start a run and read the plan.</li>
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          1 · CREATE THE TENANT — POST /api/v1/tenants
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Names may hold letters, digits, dot, underscore and dash. A duplicate is a 422; storing
          parameters for a tenant also registers it.
        </Typography>
        <Snippet
          code={`curl -s -X POST ${HOST}/api/v1/tenants \\
  -H 'Content-Type: application/json' \\
  -d '{"tenant": "${tenant}"}'`}
        />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          2 · NAME THE FLEETS — POST /api/v1/fleets
        </Typography>
        <Typography variant="body2" color="text.secondary">
          A posted world carries only fleet <em>ids</em> on its routes. Registering names here is
          what lets the run screen offer “Acme Cars (12)” instead of “Fleet 12”. Fleet id 0 is the
          routes carrying no fleet — a Myle driver&apos;s own, shown as the Myle fleet. Durable —
          it survives a restart.
        </Typography>
        <Snippet
          code={`curl -s -X POST ${HOST}/api/v1/fleets \\
  -H 'Content-Type: application/json' \\
  -d '{"tenant": "${tenant}", "fleets": [
        {"id": 12, "name": "Acme Cars"},
        {"id": 77, "name": "Metro Rides"}
      ]}'`}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Both <code>id</code> and <code>name</code> are required on every entry.{' '}
          <code>GET /api/v1/fleets?tenant={tenant}</code> lists one page of them — every listing
          takes <code>&amp;offset=</code> and <code>&amp;limit=</code> (100 by default, 500 at
          most) and answers <code>{'{total, offset, limit, …}'}</code>;{' '}
          <code>DELETE</code> with <code>&amp;fleet_id=12</code> drops one, without it drops them all.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          3 · REGISTER THE DRIVERS AND VEHICLES — POST /api/v1/drivers
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Say once what each <code>driver_id</code> drives instead of repeating it on every route.
          A route posted without <code>seats</code>, <code>wav_car</code>, <code>car_type</code> or{' '}
          <code>car_id</code> picks them up from here; a route that states its own always wins.
          Durable — it survives a restart.
        </Typography>
        <Snippet
          code={`curl -s -X POST ${HOST}/api/v1/drivers \\
  -H 'Content-Type: application/json' \\
  -d '{"tenant": "${tenant}", "drivers": [
        {"id": 55, "name": "Dana",
         "vehicle": {"car_id": 9, "seats": 4, "wav_seats": 2, "wav_car": true,
                     "car_type": "ambulette", "plate": "ABC-123",
                     "make_model": "Ford Transit"}}
      ]}'`}
        />
        <Fields rows={DRIVER_REQUIRED} optional={DRIVER_OPTIONAL} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <code>car_type</code> must be one of <code>ambulance</code>, <code>ambulette</code>,{' '}
          <code>fhv</code>, <code>green_taxi</code>, <code>minivan</code>, <code>sedan</code>,{' '}
          <code>suv</code>, <code>van</code> — wheelchair access is <code>wav_car</code> and{' '}
          <code>wav_seats</code>, never a car type.{' '}
          <code>GET /api/v1/drivers?tenant={tenant}</code> lists one page of drivers with their
          vehicles and the <code>route_ids</code> each is currently driving — a driver a route
          names but nobody registered comes back too, as <code>registered: false</code>.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          4 · POST THE RIDES — POST /api/v1/rides
        </Typography>
        <Snippet
          code={`curl -s -X POST ${HOST}/api/v1/rides \\
  -H 'Content-Type: application/json' \\
  -d '{"tenant": "${tenant}", "rides": [
        {"id": 9001,
         "pickup_at": "2026-08-20 14:00:00", "dropoff_at": "2026-08-20 14:30:00",
         "pickup_lat": 40.71, "pickup_lon": -74.00,
         "dropoff_lat": 40.75, "dropoff_lon": -73.98,
         "company_id": 17}
      ]}'`}
        />
        <Fields rows={RIDE_REQUIRED} optional={RIDE_OPTIONAL} />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          5 · POST THE ROUTES — POST /api/v1/routes
        </Typography>
        <Snippet
          code={`curl -s -X POST ${HOST}/api/v1/routes \\
  -H 'Content-Type: application/json' \\
  -d '{"tenant": "${tenant}", "routes": [
        {"id": 601, "driver_id": 55, "fleet_id": 12,
         "from_datetime": "2026-08-20 08:00:00", "to_datetime": "2026-08-20 20:00:00",
         "starting_zip_code": "11201"}
      ]}'`}
        />
        <Fields rows={ROUTE_REQUIRED} optional={ROUTE_OPTIONAL} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          A route says who is driving and when — how to route is a parameter (Config tab), not a
          route field. A route posted without vehicle fields takes them from the registered
          driver, at submit time rather than at run time.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          6 · PLAN IT — POST /api/v1/runs
        </Typography>
        <Snippet
          code={`curl -s -X POST ${HOST}/api/v1/runs \\
  -H 'Content-Type: application/json' \\
  -d '{"from_datetime": "2026-08-20", "tenant": "${tenant}"}'

curl -s ${HOST}/api/v1/runs/<run_id>/routes   # outcome + ordered plan per route`}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <code>from_datetime</code> picks an America/New_York calendar day; a route joins that
          run when its <code>from_datetime</code> falls inside the day. Planning always happens as
          of the start of that day rather than right now, so a day that has already passed plans
          the same however long after it — and{' '}
          <code>POST /api/v1/runs/&lt;run_id&gt;/rerun</code> re-plans a finished run under a
          different configuration, with{' '}
          <code>GET /api/v1/runs/&lt;run_id&gt;/compare</code> for what that changed.
        </Typography>
      </Paper>


      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          A RUN, PUSHED TO YOU — THE PLATFORM CALLBACKS
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Each callback has its own URL, set per tenant on the Config tab for “{tenant}” —{' '}
          <code>route_completed_url</code> gets one notification per finished route (assigned,
          skipped or failed alike) instead of you polling the run, and{' '}
          <code>routing_started_url</code> and <code>routing_finished_url</code> bracket the night —{' '}
          <code>routing.started</code> before the first route is planned and{' '}
          <code>routing.finished</code> once the day is done — both carrying{' '}
          <code>{'{run_id, tenant, day}'}</code>. Setting one does not enable the others, and the
          day pair is only made alongside <code>route_completed_url</code> — clearing and applying
          a day nothing was staged into would empty it.
        </Typography>
        <Snippet
          code={`{
  "event": "route.completed",
  "run_id": "42",
  "tenant": "${tenant}",
  "route_id": 601,
  "status": "completed",
  "outcome": "finished",
  "rides_assigned": 3,
  "pools_assigned": 1,
  "wall_ms": 812,
  "error_code": null,
  "rides": [
    {
      "ride_id": 9001,
      "pick_up_at": "2026-08-20 14:05:30",
      "drop_off_at": "2026-08-20 14:42:00",
      "deadhead_miles": 2.5,
      "deadhead_seconds": 420,
      "pooled": false
    }
  ]
}`}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <code>status</code> is <code>completed</code> when the route planned and the rides are
          yours to stage; anything else means leave the route alone. Every request carries{' '}
          <code>X-Routing-Event: &lt;event&gt;</code>. Set the tenant&rsquo;s one{' '}
          <code>secret</code> too — it signs all three — and each request also carries{' '}
          <code>X-Routing-Signature: sha256=&lt;HMAC-SHA256 of the body&gt;</code> — verify it
          against the raw bytes you received, not a re-serialised copy.
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          Route notifications go out off the planning thread with a 10s timeout and no retries: a
          slow or refused endpoint costs the notification, never the route. Answer 2xx quickly and
          do the work after. A refused <code>routing.started</code> is the exception — it is what
          clears the day, so the run stops there unless the tenant&rsquo;s nightly schedule says to
          plan anyway.
        </Alert>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          RULES THAT BITE
        </Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 3, m: 0 }}>
          <li>
            <strong>Datetimes are UTC</strong> <code>&quot;yyyy-MM-dd HH:mm:ss&quot;</code>, while
            the planned day is New York — an 8pm NY shift is <code>00:00</code> the next day.
          </li>
          <li>
            <strong>Batches are all-or-nothing and upsert by id.</strong> One bad entry rejects the
            whole request with a 422 naming it (<code>rides[2].pickup_lat is required</code>), and
            a repeated id inside one request is itself an error.
          </li>
          <li>
            <strong>Everything you post is saved</strong> — tenants, parameter rules, fleets,
            drivers, rides and routes all survive a restart. Posting an id again replaces that
            entry, so re-posting is how you edit one.
          </li>
          <li>
            <strong>A route is saved resolved.</strong> The registered vehicle is folded in as
            the route is saved, so re-registering the driver later leaves routes already posted
            alone — re-post a route to pick up the new vehicle.
          </li>
          <li>
            <strong>Omitting <code>tenant</code> means <code>&quot;default&quot;</code></strong>,
            not “all tenants”.
          </li>
          <li>
            Every error is <code>{'{"error": "…", "status_code": n}'}</code> — 422 for a bad body,
            404 for an unknown run or path.
          </li>
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          The World tab does all of this from the browser — paste the same JSON arrays there
          instead of curl, and it posts them for the picked tenant.
        </Alert>
      </Paper>
    </Stack>
  );
};

export default ApiGuidePage;
